import { getUnixTime } from 'date-fns';
import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { getPrepaidPaymentInfo } from '../ChargePrepaid/ChargePrepaidHelper';
import {
  findRelevantNextWarranty,
  findWarrantyPaymentMethod,
  getExpiredWarranties,
  getValidWarranties,
  getWarrantiesMissDocument,
  listValidWarranties,
} from '../WarrantyManagement/WarrantyManagementHelper';
import { Home } from './Home/Home';
import { linkDriveOwnerApi } from '@/api/infoApi';
import {
  getSubscriptionInheritanceStatusApi,
  getSubscriptionStatusApi,
} from '@/api/subscription';
import { PageTemplateV2 } from '@/components/uiParts/Template/PageTemplate/PageTemplateV2';
import { isApp } from '@/config/device';
import {
  AuthInfoState,
  initialState,
  SelectedTenant,
} from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { deliveryCompleteFlg } from '@/consts/car';
import { carAssetValueServiceFlag, carWashServiceFlag } from '@/consts/carWash';
import {
  connectorJob,
  delFlg,
  jobUpdatingLabel,
  linkDriveSetupStatus,
  linkDriveStatus,
  setupProcessingStatus,
  updatePromiseResolveFlg,
} from '@/consts/linkDrive';
import { chargeFlg } from '@/consts/payment';
import { affiliateId as AFFILIATE_ID } from '@/consts/shop';
import { url } from '@/consts/url';
import { useProactiveMessage } from '@/hooks/api/ai/useProactiveMessage';
import { useFetchBanners } from '@/hooks/api/banner/useFetchBanners';
import { useCustomerInfo } from '@/hooks/api/customer/useCustomerInfo';
import { useLoginCount } from '@/hooks/api/customer/useLoginCount';
import { useFetchServiceCategories } from '@/hooks/api/service/useFetchServiceCategories';
import { useEffectiveAffiliateId } from '@/hooks/useEffectiveAffiliateId';
import { useLinkDriveSetupProgress } from '@/hooks/useLinkDriveSetupProgress';
import { Affiliate } from '@/types/api/authApi';
import {
  BillInformation,
  Car,
  PaymentMethod,
  Prepaid,
  Warranty,
} from '@/types/api/customerApi';
import { LinkDriveOwner } from '@/types/api/infoApi';
import {
  SubscriptionInheritanceStatus,
  SubscriptionStatus,
} from '@/types/api/subscription';
import { GmoRedirectInfo } from '@/types/payment';
import { autoLoginHelper } from '@/utils/autoLoginHelper';
import { checkImpactInfo } from '@/utils/checkECall';
import {
  isCustomerInfoAffiliateStale,
  refreshCustomerInfoForAffiliate,
} from '@/utils/customerInfoSync';
import { maybeShowMigrationPrompt } from '@/utils/migrationPrompt';
import { paymentStorage } from '@/utils/paymentStorage';
import { hidePreloader, showPreloader } from '@/utils/preloader';
import { storeDispatch } from '@/utils/store';
import {
  getPreferredCurrentWarranty,
  getRepresentativeAppendWarranty,
} from '@/utils/warranty';
// 下記importを消去しないように注意
import '@/utils/permissionHelper';
import '@/utils/deeplinkHelper';
import '@/utils/autoLoginHelper';

interface HomePageProps {
  f7router: Router.Router;
  status?: string;
  gmoRedirectInfo?: GmoRedirectInfo;
}

type GmoRestoreContext = {
  customerId: number;
  token: string;
  carId: number;
  orderId: number;
  loginType: string;
  isLoggedInToMaikuru: boolean;
};

export const HomePage: FC<HomePageProps> = ({
  f7router,
  status,
  gmoRedirectInfo,
}) => {
  const authInfo = useStore(store, 'authInfo') as AuthInfoState;
  const selectedCar = useStore(store, 'carItemInfo') as Required<Car>;
  const isInfoLoaded = useStore(store, 'getIsInfoLoaded') as boolean;
  const affiliatesInfo = useStore(store, 'affiliatesInfo') as Affiliate[];
  const globalTheme = useStore(store, 'globalTheme') as number | null;
  const isImpactInfoChecked = useStore(
    store,
    'getIsImpactInfoChecked',
  ) as boolean;
  const paymentMethods = useStore(
    store,
    'getPaymentMethodsInfo',
  ) as PaymentMethod[];
  const billsInfo = useStore(store, 'getBillInfo') as BillInformation[];
  const { warranties, nextWarranties } = store.state;
  const ownedCars = useStore(store, 'carsInfo') as Required<Car>[];
  const prepaidInfo = useStore(
    store,
    'getFirstAdvancePaymentsInfo',
  ) as Required<Prepaid>;
  const linkDriveOwnerInfo = useStore(
    store,
    'linkDriveOwnerInfo',
  ) as LinkDriveOwner;
  const selectedTenant = useStore(store, 'getSelectedTenant') as SelectedTenant;
  const [isOpenCarList, setIsOpenCarList] = useState(false);
  const [isSwitchingCar, setIsSwitchingCar] = useState(false);
  const [gmoRestoreContext, setGmoRestoreContext] =
    useState<GmoRestoreContext | null>(null);
  const [gmoRestorePending, setGmoRestorePending] = useState(false);
  const [gmoRestoreFailed, setGmoRestoreFailed] = useState(false);
  const isSwitchingCarRef = useRef(false);
  const [isWarrantyExpired, setIsWarrantyExpired] = useState(false);
  const [isWarrantiesMissDocument, setIsWarrantiesMissDocument] =
    useState(false);
  const [unregistPaymentWarranty, setUnregistPaymentWarranty] =
    useState<Warranty>();
  const [isShowPrepaidInfo, setIsShowPrepaidInfo] = useState(false);
  const [isBrainTrainingDisplay, setIsBrainTrainingDisplay] = useState(false);
  const [isMileageDisplay, setIsMileageDisplay] = useState(false);
  const [isRegisterPaymentWarranty, setIsRegisterPaymentWarranty] =
    useState(false);
  const information = store.state.notificationsInfo;
  const { callCountLoginApi } = useLoginCount(authInfo.m_customer_id);
  const { recursiveJobStatus, updateModeReservation, updateECU } =
    useLinkDriveSetupProgress(store.state.linkDriveAbortController.signal);
  // プロアクティブメッセージ取得
  const { proactiveMessage, fetchProactiveMessage } = useProactiveMessage();
  let affiliatesSwitch = affiliatesInfo;

  if (globalTheme) {
    const currentAffiliate = affiliatesInfo.find(
      (affiliate) => affiliate.id === authInfo.m_customer_id,
    );
    affiliatesSwitch = affiliatesInfo.filter(
      (affiliate) =>
        affiliate.id === authInfo.m_customer_id ||
        currentAffiliate?.affiliate_app_type === affiliate.affiliate_app_type,
    );
  }

  const isShowCarWashService =
    store.state.customerInfo.carwash_service_flg ===
    carWashServiceFlag.INSTALLED;

  const isShowCarAssetValue =
    store.state.customerInfo.car_asset_value_flg ===
    carAssetValueServiceFlag.INSTALLED;

  // サブスク契約状態
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const [subscriptionStatusVal, setSubscriptionStatusVal] = useState<
    number | undefined
  >(undefined);
  const [subscriptionApplyStatus, setSubscriptionApplyStatus] = useState<
    number | undefined
  >(undefined);
  const [subscriptionNoticeId, setSubscriptionNoticeId] = useState<
    number | undefined
  >(undefined);
  const [hasSubscriptionNotice, setHasSubscriptionNotice] = useState(false);
  const [subscriptionInheritanceStatus, setSubscriptionInheritanceStatus] =
    useState<SubscriptionInheritanceStatus | null>(null);
  const [subscriptionTStockCarId, setSubscriptionTStockCarId] = useState<
    number | undefined
  >(undefined);
  const [subscriptionId, setSubscriptionId] = useState<number | undefined>(
    undefined,
  );
  const setSubscriptionState = useCallback(
    (
      currentSubscription?: SubscriptionStatus | null,
      pendingSubscription?: SubscriptionStatus | null,
    ) => {
      const isCurrentVehicleSubscribed =
        !!currentSubscription &&
        (currentSubscription.apply_status === 3 ||
          (currentSubscription.apply_status === 0 &&
            currentSubscription.status === 1));
      const noticeSubscription = isCurrentVehicleSubscribed
        ? currentSubscription
        : pendingSubscription || currentSubscription || null;

      setHasSubscription(isCurrentVehicleSubscribed);
      setHasSubscriptionNotice(!!noticeSubscription);
      setSubscriptionNoticeId(noticeSubscription?.t_subscription_id);
      setSubscriptionStatusVal(noticeSubscription?.status);
      setSubscriptionApplyStatus(noticeSubscription?.apply_status);
    },
    [],
  );
  // サブスク契約状態の取得。
  // onPageAfterIn からも呼べるよう useCallback で切り出している。
  // ホームがキャッシュ再利用されると依存配列が変わらず再取得されないため、
  // 契約完了後に戻ってきても hasSubscription が古い値のままになり、
  // 「サブスク契約中」ではなく「保証管理」が表示されてしまう。
  const refetchSubscriptionStatus = useCallback(async () => {
    if (!authInfo?.m_customer_id) return;

    const fetchSubscriptionStatus = async () => {
      const applyResult = (data: {
        subscription?: SubscriptionStatus | null;
        pending_subscription?: SubscriptionStatus | null;
      }) => {
        const sub = data.subscription;
        const pending = data.pending_subscription;
        setSubscriptionState(sub, pending);
        setSubscriptionTStockCarId(
          (sub as any)?.t_stock_car_id ?? (pending as any)?.t_stock_car_id,
        );
        setSubscriptionId(sub?.t_subscription_id ?? pending?.t_subscription_id);
      };

      // 選択中の車両で契約状態を取得（失敗時1回リトライ）
      const fetchWithRetry = async (tStockCarId?: number | null) => {
        try {
          const res = await getSubscriptionStatusApi(
            authInfo.m_customer_id,
            tStockCarId,
          );
          if (res.data.success) return res;
        } catch {
          // 1回目失敗 → リトライ
        }
        return getSubscriptionStatusApi(authInfo.m_customer_id, tStockCarId);
      };

      try {
        const res = await fetchWithRetry(selectedCar?.t_stock_car_id);
        if (res.data.success && res.data.data) {
          const sub = res.data.data.subscription;
          const pending = res.data.data.pending_subscription;

          // 車両IDで見つからない場合、車両ID なしで未決済のサブスクを検索
          // （申込後に車検証未登録で t_stock_car_id が NULL のケース）
          if (!sub && !pending) {
            const globalRes = await fetchWithRetry();
            if (
              globalRes.data.success &&
              globalRes.data.data &&
              (globalRes.data.data.subscription ||
                globalRes.data.data.pending_subscription)
            ) {
              // フォールバック由来の場合、契約済みサブスクや
              // 別車両に紐付いたサブスクは通知対象外とする（車両選択画面と整合）
              let fallbackSub = globalRes.data.data.subscription ?? null;
              let fallbackPending =
                globalRes.data.data.pending_subscription ?? null;
              const isActiveSub = (s: SubscriptionStatus | null | undefined) =>
                !!s &&
                (s.apply_status === 3 ||
                  (s.apply_status === 0 && s.status === 1));
              const isLinkedToOtherCar = (
                s: SubscriptionStatus | null | undefined,
              ) => !!s && !!(s as any).t_stock_car_id;
              const hasActiveSubElsewhere =
                isActiveSub(fallbackSub) || isActiveSub(fallbackPending);
              if (isActiveSub(fallbackSub)) fallbackSub = null;
              if (isActiveSub(fallbackPending)) fallbackPending = null;
              if (isLinkedToOtherCar(fallbackSub)) fallbackSub = null;
              if (isLinkedToOtherCar(fallbackPending)) fallbackPending = null;
              // 別車両で契約済みサブスクが存在する場合、車両未紐付のpendingは
              // ゴーストレコードの可能性が高いため通知対象外とする
              // ただし apply_status=2（決済待ち）は実際の申込なので除外しない
              if (hasActiveSubElsewhere) {
                if (
                  fallbackPending &&
                  !(fallbackPending as any).t_stock_car_id &&
                  fallbackPending.apply_status !== 2
                )
                  fallbackPending = null;
                if (
                  fallbackSub &&
                  !(fallbackSub as any).t_stock_car_id &&
                  fallbackSub.apply_status !== 2
                )
                  fallbackSub = null;
              }

              applyResult({
                subscription: fallbackSub,
                pending_subscription: fallbackPending,
              });
              // フォールバック由来 = 別車両のサブスクの可能性があるため
              // 「契約中」扱いにしない（mypage への遷移を防止）
              setHasSubscription(false);
              return;
            }
          }

          applyResult(res.data.data);
        }
      } catch (e) {
        console.error('サブスク状態チェックエラー', e);
      } finally {
        // 再取得中も false には戻さない（戻すとスケルトンが二度点滅する）
        setSubscriptionLoaded(true);
      }
    };

    await fetchSubscriptionStatus();
  }, [
    authInfo?.m_customer_id,
    selectedCar?.t_stock_car_id,
    setSubscriptionState,
  ]);

  useEffect(() => {
    refetchSubscriptionStatus();
  }, [refetchSubscriptionStatus]);

  useEffect(() => {
    if (authInfo?.m_customer_id) {
      fetchProactiveMessage();
    }
  }, [authInfo?.m_customer_id, fetchProactiveMessage]);

  useEffect(() => {
    const fetchInheritanceStatus = async () => {
      if (!authInfo?.m_customer_id || !selectedCar?.t_stock_car_id) {
        setSubscriptionInheritanceStatus(null);
        return;
      }

      try {
        const res = await getSubscriptionInheritanceStatusApi(
          authInfo.m_customer_id,
          selectedCar.t_stock_car_id,
        );
        if (res.data.success && res.data.data?.eligible) {
          setSubscriptionInheritanceStatus(res.data.data);
          return;
        }
      } catch {
        // サブスク本導線は維持するため、引き継ぎ判定失敗時は通常表示に倒す
      }
      setSubscriptionInheritanceStatus(null);
    };

    fetchInheritanceStatus();
  }, [authInfo?.m_customer_id, selectedCar?.t_stock_car_id]);

  // 選択中の車両が保証加入済みかどうか（二重契約防止）
  const hasActiveWarranty = getValidWarranties(warranties).length > 0;

  // サブスクサービス導入済みか（m_mt_services × t_mt_service_usages）
  const hasSubscriptionService =
    (store.state.customerInfo as any).subscription_service_flg === 1;

  // サブスクボタン表示条件: サービス導入済み かつ 保証未加入の場合に申込導線を表示
  // ただし手続き中（apply_status=1,2）の場合は保証有無に関わらずサブスクボタンを表示
  const hasPendingSubscription =
    hasSubscriptionNotice &&
    (subscriptionApplyStatus === 1 ||
      subscriptionApplyStatus === 2 ||
      (subscriptionApplyStatus === 0 && subscriptionStatusVal === 2));
  const isShowSubscription =
    hasSubscriptionService && (!hasActiveWarranty || hasPendingSubscription);

  const isShowSwitchCar = true;

  const { getCustomerWithParams, refetch } = useCustomerInfo(
    {
      m_customer_id: authInfo.m_customer_id,
    },
    !status,
    true,
  );

  const timestamp = getUnixTime(new Date());
  const newsList = information.filter((item) => {
    return (
      getUnixTime(new Date(item.start_date)) <= timestamp &&
      getUnixTime(new Date(item.end_date)) >= timestamp
    );
  });

  const displayNews = newsList.length > 0 ? newsList.slice(0, 1)[0] : undefined;
  const notificationCount = newsList.length;
  const warrantyStatusText = getRepresentativeAppendWarranty(
    getValidWarranties(warranties)[0],
  )?.warranty_name;
  const affiliate_id = store.state.customerInfo.m_affiliate_id;
  // const affiliate_id = 331;
  // バナー・サービスカテゴリは「ショッピング先」の法人で出し分ける（テナント切り替えを反映）
  const shoppingAffiliateId = useEffectiveAffiliateId();
  // ホームはセクション単位でスケルトン表示するため全画面プリローダーは出さない
  const { categories, isLoading: isCategoriesLoading } =
    useFetchServiceCategories({
      m_affiliate_id: shoppingAffiliateId,
      disablePreloader: true,
    });
  const displayCategories =
    shoppingAffiliateId === AFFILIATE_ID.YAMADA ||
    shoppingAffiliateId === AFFILIATE_ID.YAMADA_2
      ? categories
      : undefined;
  const { banners, isLoading: isBannersLoading } = useFetchBanners({
    m_affiliate_id: shoppingAffiliateId,
    limit: 5,
    disablePreloader: true,
  });

  // 現在の customerInfo がどの法人向けに取得されたか。
  // subscription_service_flg は切り替え先法人の導入状況で再判定されるため、
  // ショッピング先と一致していない間は「まだ分からない」状態として扱う。
  const customerInfoAffiliateId = useStore(
    store,
    'getCustomerInfoAffiliateId',
  ) as number | null;
  const isCustomerInfoStale = isCustomerInfoAffiliateStale(
    shoppingAffiliateId,
    customerInfoAffiliateId,
  );

  // 車両切り替え中に認証が先に更新されると、再取得画面への切り替えで
  // Framework7 がルートへ移した Popup が残る。画面を隠す前に明示的に閉じる。
  useEffect(() => {
    if (!isCustomerInfoStale || !isOpenCarList) return;
    f7.popup.close('#ResendSms', false);
    setIsOpenCarList(false);
  }, [isCustomerInfoStale, isOpenCarList]);

  // ズレていたら取り直す（テナント切り替え直後・切り替え済みで再遷移した場合の両方をカバー）
  // 取得が失敗しても、法人文脈が一致するまでは古い顧客情報を表示しない。
  const [isStaleCheckGivenUp, setIsStaleCheckGivenUp] = useState(false);
  useEffect(() => {
    if (!isCustomerInfoStale) {
      setIsStaleCheckGivenUp(false);
      return;
    }
    // 車両切替側が新しい顧客情報を取得中。ここでも取得すると旧画面の
    // refetch と応答が競合し、画面と顧客情報の法人が再び食い違う。
    if (isSwitchingCar) return;
    let isCurrent = true;
    setIsStaleCheckGivenUp(false);
    void refreshCustomerInfoForAffiliate(
      refetch,
      authInfo.m_customer_id,
      shoppingAffiliateId,
    )
      .then((synced) => {
        if (isCurrent && synced === false) setIsStaleCheckGivenUp(true);
      })
      .catch((error) => {
        console.error(error);
        if (isCurrent) setIsStaleCheckGivenUp(true);
      });
    return () => {
      isCurrent = false;
    };
  }, [
    isCustomerInfoStale,
    shoppingAffiliateId,
    authInfo.m_customer_id,
    refetch,
    isSwitchingCar,
  ]);

  const retryCustomerInfoSync = async () => {
    setIsStaleCheckGivenUp(false);
    try {
      const synced = await refreshCustomerInfoForAffiliate(
        refetch,
        authInfo.m_customer_id,
        shoppingAffiliateId,
      );
      if (synced === false) setIsStaleCheckGivenUp(true);
    } catch (error) {
      console.error(error);
      setIsStaleCheckGivenUp(true);
    }
  };

  const clearSelectedTenantAfterSyncError = async () => {
    setIsStaleCheckGivenUp(false);
    await storeDispatch('setSelectedTenant', 'selectedTenant', null);
  };

  const handleClickRegistCar = () => {
    storeDispatch('setRegisterUser', 'registerUser', {
      ...initialState.registerUser,
      affiliate_id: Number(affiliate_id),
      currentStep: 3,
      sourcePage: paths.home,
      startStep: 3,
    });
    f7.view.main.router.navigate(paths.registerNewMember);
  };

  const handleClickListImage = async (
    car: Affiliate['cars'][0] | null,
    affiliateID: number,
  ) => {
    if (isSwitchingCarRef.current) return;
    isSwitchingCarRef.current = true;
    let preloaderVisible = false;
    try {
      // 顧客認証の更新で Home が再取得画面に切り替わる前に閉じる。
      // ポップアップを表示したまま Home を差し替えると backdrop が残り、
      // 画面全体を覆って操作できなくなる。
      f7.popup.close('#ResendSms', false);
      setIsOpenCarList(false);
      setIsSwitchingCar(true);
      showPreloader();
      preloaderVisible = true;
      let carsList = ownedCars;
      // affiliateID は車両の所属顧客ID。別顧客への切り替えでは
      // switchCustomerAuth → setAuthInfo がショッピング先選択を解除する。
      if (affiliateID !== authInfo.m_customer_id) {
        const customerInfo = await getCustomerWithParams(
          {
            m_customer_id: affiliateID,
          },
          (c) => {
            if (car) {
              return car.m_car_id == null
                ? car.t_order_id === c.t_order_id
                : car.m_car_id === c.m_car_id;
            }
            return true;
          },
        );
        if (!customerInfo) return;
        if (customerInfo.cars) {
          carsList = customerInfo.cars as Required<Car>[];
        }
      }
      const carInfo = car
        ? carsList.find((c) =>
            car.m_car_id == null
              ? car.t_order_id === c.t_order_id
              : car.m_car_id === c.m_car_id,
          )
        : initialState.carItemInfo;
      if (!carInfo) {
        setIsOpenCarList(false);
        return;
      }
      await storeDispatch('setCarItemInfo', 'carItemInfo', carInfo);
      await storeDispatch(
        'setWarranties',
        'warranties',
        carInfo?.warranties || [],
      );
      await storeDispatch(
        'setNextWarranties',
        'nextWarranties',
        carInfo?.next_warranty || [],
      );
      const preferredWarranty = getPreferredCurrentWarranty(carInfo.warranties);
      await storeDispatch(
        'setCurrentWarranty',
        'currentWarranty',
        preferredWarranty,
      );
      await storeDispatch(
        'setNextWarranty',
        'nextWarranty',
        findRelevantNextWarranty(preferredWarranty, carInfo?.next_warranty),
      );
      const { t_stock_car_id } = carInfo;
      setIsOpenCarList(false);
      hidePreloader();
      preloaderVisible = false;
      const { data: linkDriveOwner } = await linkDriveOwnerApi({
        params: {
          customer_id: store.state.authInfo.m_customer_id,
          stock_car_id: t_stock_car_id,
          del_flg: 0,
        },
      });

      store.state.linkDriveAbortController.abort();
      await storeDispatch(
        'setOwnerId',
        'ownerId',
        linkDriveOwner?.data?.link_drive_owner?.owner_id ||
          initialState.linkDriveOwnerInfo.owner_id,
      );
      await storeDispatch(
        'setSerialNumberInput',
        'serialNumberInput',
        initialState.serialNumberInput,
      );
      await storeDispatch(
        'setSerialNumberDb',
        'serialNumberDb',
        linkDriveOwner?.data?.link_drive_owner?.serial_no ||
          initialState.linkDriveOwnerInfo.serial_no,
      );
      await storeDispatch(
        'setLinkDriveUpdating',
        'linkDriveUpdating',
        initialState.linkDriveUpdating,
      );
      await storeDispatch(
        'setLinkDriveAbortController',
        'linkDriveAbortController',
        new AbortController(),
      );
      await storeDispatch('setLinkDriveProgress', 'linkDriveProgress', 0);
      await storeDispatch(
        'setLinkDriveOwnerInfo',
        'linkDriveOwnerInfo',
        linkDriveOwner?.data?.link_drive_owner ||
          initialState.linkDriveOwnerInfo,
      );
      storeDispatch(
        'setPrepaidChargePayment',
        'prepaidChargePayment',
        initialState.prepaidChargePayment,
      );
      if (
        linkDriveOwner?.error &&
        linkDriveOwner.error.message !== 'LinkDriveOwnerなし'
      ) {
        throw new Error(linkDriveOwner.error.message);
      }
      // AD向け車両のログインカウント
      await callCountLoginApi(carInfo, affiliateID);
    } finally {
      isSwitchingCarRef.current = false;
      setIsSwitchingCar(false);
      if (preloaderVisible) hidePreloader();
    }
  };

  const handleClickNavigationNews = () => {
    f7.view.main.router.navigate(paths.news);
  };

  const handleClickMaintenanceButton = () => {
    f7.view.main.router.navigate(paths.maintenanceGuide);
  };

  const handleClickNews = (id: string) => {
    f7.view.main.router.navigate(paths.noticeDetail(id), {
      props: { item: displayNews },
    });
  };

  const changeBannerIsDisplay = (car?: Required<Car>) => {
    const isLinkDriveStatus =
      car?.linkdrive_status === linkDriveStatus.FORMAL_REGISTRATION ||
      car?.linkdrive_status === linkDriveStatus.AGREEMENT;
    const isAdMileageDisplayFlg =
      car?.ad_mileage_display_flg === linkDriveStatus.TEMPORARY_REGISTRATION;
    setIsBrainTrainingDisplay(isLinkDriveStatus);
    setIsMileageDisplay(isLinkDriveStatus || isAdMileageDisplayFlg);
  };

  const handlePaymentRegistAlert = () => {
    if (!unregistPaymentWarranty) return;
    storeDispatch(
      'setCurrentWarranty',
      'currentWarranty',
      unregistPaymentWarranty,
    );
    store.dispatch('setPaymentTempData', {
      type: 'warranty',
      openDialogName: paths.registerPayment(
        'warranty',
        unregistPaymentWarranty.warranty_no,
      ),
      isTransition: false,
    });
    f7.view.main.router.navigate(
      paths.registerPayment('warranty', unregistPaymentWarranty.warranty_no),
      {
        props: {
          redirectPage: 'home',
        },
      },
    );
  };

  const handlePaymentRegistAlertPrepaid = () => {
    paymentStorage.setItem('m_prepaid_id', JSON.stringify(prepaidInfo.id));
    paymentStorage.setItem('prevPage', 'prepaid');
    f7.view.main.router.navigate(
      paths.registerPayment('prepaid', String(prepaidInfo.id)),
      {
        props: {
          redirectPage: 'home',
        },
      },
    );
  };

  const handleOnRefech = async (cb: () => void) => {
    try {
      // 顧客情報とサブスク契約状態、プロアクティブメッセージを並列で最新化する。
      // サブスク状態を取り直さないと、契約完了後にホームへ戻った際に
      // 「サブスク契約中」ではなく「保証管理」が表示されたままになる。
      await Promise.all([
        refetch(),
        refetchSubscriptionStatus(),
        fetchProactiveMessage(),
      ]);
      cb();
    } catch (e) {
      console.error('Error while refetching: ', e);
    }
  };

  useEffect(() => {
    if (
      isApp &&
      !store.state.linkDriveCheck &&
      linkDriveOwnerInfo.id &&
      linkDriveOwnerInfo.del_flg === delFlg.NOT_DELETE &&
      linkDriveOwnerInfo.setup_status !=
        linkDriveSetupStatus.MODE_RESERVATION_COMPLETE
    ) {
      window.location.href =
        url.NATIVE_BASE_URL + '/linkdrive/setup_incomplete/';
      storeDispatch('setLinkDriveCheck', 'linkDriveCheck', true);
    }
  }, [linkDriveOwnerInfo]);

  useEffect(() => {
    // GMOリダイレクト戻り時(status付き)のみ自動表示する。
    // statusなしで発火するとGMO後にタブバーでホームに遷移した際に
    // RegisterPaymentポップアップが誤表示される。
    if (!status) return;
    const mCustomerId = paymentStorage.getItem('m_customer_id');
    if (
      !mCustomerId ||
      !authInfo.isLogin ||
      !unregistPaymentWarranty?.warranty_no
    )
      return;
    f7.views.main.router.navigate(
      paths.registerPayment('warranty', unregistPaymentWarranty?.warranty_no),
      {
        props: {
          redirectPage: 'warranty',
        },
      },
    );
    paymentStorage.removeItem('token');
    paymentStorage.removeItem('m_customer_id');
    paymentStorage.removeItem('globalTheme');
    paymentStorage.removeItem('karteUrlTheme');
    paymentStorage.removeItem('isLoggedInToMaikuru');
    paymentStorage.removeItem('prepaid_registration_info');
  }, [authInfo.isLogin, unregistPaymentWarranty?.warranty_no]);

  useEffect(() => {
    changeBannerIsDisplay(selectedCar);
  }, [selectedCar]);

  useEffect(() => {
    let validWarranties = getValidWarranties(warranties);
    if (
      selectedCar?.delivery_complete_flg ==
        deliveryCompleteFlg.BEFORE_DELIVERY &&
      validWarranties.length === 0
    ) {
      validWarranties = warranties;
    }
    const unregisteredWarranties = validWarranties.filter(
      (warranty) => !findWarrantyPaymentMethod(warranty, paymentMethods),
    );
    setUnregistPaymentWarranty(unregisteredWarranties[0]);
    setIsWarrantyExpired(
      getExpiredWarranties(warranties).length > 0 &&
        getValidWarranties(warranties).length === 0,
    );
    setIsWarrantiesMissDocument(
      getWarrantiesMissDocument(warranties).length > 0,
    );
  }, [paymentMethods, warranties, selectedCar?.delivery_complete_flg]);

  useEffect(() => {
    const payment = getPrepaidPaymentInfo(prepaidInfo, paymentMethods);
    const isShowPrepaidPaymentInfo =
      !payment && prepaidInfo?.charge_flg === chargeFlg;
    setIsShowPrepaidInfo(isShowPrepaidPaymentInfo);
  }, [prepaidInfo, paymentMethods]);

  const restoreGmoCustomer = async (context: GmoRestoreContext) => {
    setGmoRestoreFailed(false);
    setGmoRestorePending(true);
    try {
      await autoLoginHelper({
        token: context.token,
        id: context.customerId,
        f7router,
        type: context.loginType,
        isLoggedInToMaikuru: context.isLoggedInToMaikuru,
        navigate: false,
      });
      const restored = await getCustomerWithParams(
        { m_customer_id: context.customerId },
        (car) =>
          car.m_car_id == null
            ? car.t_order_id === context.orderId
            : car.m_car_id === context.carId,
      );
      if (restored?.customer?.id !== context.customerId) {
        throw new Error('GMO復帰後の顧客情報を復元できませんでした');
      }
      setGmoRestoreContext(null);
    } catch (error) {
      console.error('GMO復帰後の顧客情報取得に失敗しました', error);
      setGmoRestoreFailed(true);
    } finally {
      setGmoRestorePending(false);
    }
  };

  useEffect(() => {
    if (status && !authInfo.isLogin) {
      const customerId = paymentStorage.getItem('m_customer_id');
      const token = paymentStorage.getItem('token');
      const m_car_id = paymentStorage.getItem('m_car_id');
      const t_order_id = paymentStorage.getItem('t_order_id');
      const loginType = paymentStorage.getItem('loginType');
      const isLoggedInToMaikuru =
        paymentStorage.getItem('isLoggedInToMaikuru') === 'true';
      if (customerId && token) {
        const context: GmoRestoreContext = {
          customerId: Number(customerId),
          token,
          carId: Number(m_car_id),
          orderId: Number(t_order_id),
          loginType: loginType || '',
          isLoggedInToMaikuru,
        };
        setGmoRestoreContext(context);
        void restoreGmoCustomer(context);
      }
      paymentStorage.removeItem('token');
      paymentStorage.removeItem('m_customer_id');
      paymentStorage.removeItem('globalTheme');
      paymentStorage.removeItem('karteUrlTheme');
      paymentStorage.removeItem('isLoggedInToMaikuru');
      paymentStorage.removeItem('prepaid_registration_info');
    }
  }, [status, authInfo, f7router]);

  useEffect(() => {
    /**
     * @description Requesting Push notification permission
     * When native app receives the request,
     * it uses onPushEnabled global function to inject the state
     * onPushEnabled function is being imported at line 14 of this file.
     * Check the implementation.
     */

    if (isApp) {
      window.location.href = url.NATIVE_BASE_URL + '/permission/push';
    }
  }, []);

  useEffect(() => {
    if (isInfoLoaded) {
      void maybeShowMigrationPrompt();
    }
  }, [isInfoLoaded]);

  useEffect(() => {
    if (isInfoLoaded && !isImpactInfoChecked) {
      checkImpactInfo(store.state.authInfo.m_customer_id);
    }
  }, [isInfoLoaded, isImpactInfoChecked]);

  useEffect(() => {
    let validWarranties = getListValidWarranties();
    const billWarranty = billsInfo.find(
      (bill) =>
        bill.warranty_no != null &&
        checkWarranties(bill.warranty_no, validWarranties) &&
        bill.next_bill_date != null,
    );
    setIsRegisterPaymentWarranty(!!(billWarranty && unregistPaymentWarranty));
  }, [
    billsInfo,
    unregistPaymentWarranty,
    warranties,
    nextWarranties,
    selectedCar?.delivery_complete_flg,
  ]);

  const checkWarranties = (warrantyNo: any, validWarranties: Warranty[]) => {
    if (!warrantyNo) return false;

    const warranty = validWarranties.find(
      (warranty) => warranty.warranty_no === warrantyNo,
    );

    return !!warranty;
  };

  const getListValidWarranties = () => {
    let validWarranties = listValidWarranties(warranties, nextWarranties);
    if (
      selectedCar?.delivery_complete_flg ==
        deliveryCompleteFlg.BEFORE_DELIVERY &&
      validWarranties.length === 0
    ) {
      validWarranties = warranties;
    }

    return validWarranties;
  };

  window.onLinkDriveJob = async (job: string, job_id: string) => {
    if (job && job_id) {
      storeDispatch(
        'setLinkDriveUpdating',
        'linkDriveUpdating',
        setupProcessingStatus.PROCESSING,
      );
      let rs = updatePromiseResolveFlg.ENDED;
      const startTime = new Date().getTime();
      rs = await recursiveJobStatus(job, job_id, startTime);
      if (
        rs === updatePromiseResolveFlg.SUCCESS &&
        job === connectorJob.FIRMWARE_UPDATE
      ) {
        rs = await updateModeReservation();
      }
      if (
        rs === updatePromiseResolveFlg.SUCCESS &&
        job !== connectorJob.ECU_LIST_UPDATE
      ) {
        const ecuStartTime = new Date().getTime();
        storeDispatch('setLinkDriveProgress', 'linkDriveProgress', 0);
        storeDispatch(
          'setLinkDriveSetupCurrent',
          'linkDriveSetupCurrent',
          jobUpdatingLabel[connectorJob.ECU_LIST_UPDATE],
        );
        rs = await updateECU(ecuStartTime);
      }
      if (isApp) {
        window.location.href =
          url.NATIVE_BASE_URL + `/linkdrive/setup_complete/`;
      }
      if (rs === updatePromiseResolveFlg.SUCCESS) {
        storeDispatch(
          'setLinkDriveUpdating',
          'linkDriveUpdating',
          setupProcessingStatus.SUCCESS,
        );
        return;
      }
      storeDispatch(
        'setLinkDriveUpdating',
        'linkDriveUpdating',
        setupProcessingStatus.INACTIVE,
      );
      storeDispatch('setLinkDriveSetupCurrent', 'linkDriveSetupCurrent', '');
      storeDispatch('setLinkDriveProgress', 'linkDriveProgress', 0);
    }
  };

  if (gmoRestoreFailed && gmoRestoreContext) {
    return (
      <PageTemplateV2 pageName="Home" title="ホーム" hideNavBar>
        <div style={{ padding: '32px 16px', textAlign: 'center' }} role="alert">
          <p>決済から戻った後の顧客情報を取得できませんでした。</p>
          <button
            type="button"
            onClick={() => void restoreGmoCustomer(gmoRestoreContext)}
          >
            再試行
          </button>
        </div>
      </PageTemplateV2>
    );
  }

  if (gmoRestorePending && gmoRestoreContext) {
    return (
      <PageTemplateV2 pageName="Home" title="ホーム" hideNavBar>
        <div
          style={{ padding: '32px 16px', textAlign: 'center' }}
          aria-live="polite"
        >
          決済から戻った後の顧客情報を確認しています...
        </div>
      </PageTemplateV2>
    );
  }

  return (
    <Home
      staleContent={
        isCustomerInfoStale ? (
          <div
            style={{ padding: '32px 16px', textAlign: 'center' }}
            aria-live="polite"
          >
            {isStaleCheckGivenUp ? (
              <>
                <p>表示先の情報を取得できませんでした。</p>
                <button
                  type="button"
                  onClick={() => void retryCustomerInfoSync()}
                >
                  再試行
                </button>
                {selectedTenant && (
                  <button
                    type="button"
                    onClick={() => void clearSelectedTenantAfterSyncError()}
                  >
                    法人の切り替えを解除
                  </button>
                )}
              </>
            ) : (
              <p>表示先の情報を確認しています...</p>
            )}
          </div>
        ) : undefined
      }
      isBasicInfoLoaded={isInfoLoaded}
      isBrainTrainingDisplay={isBrainTrainingDisplay}
      isMileageDisplay={isMileageDisplay}
      selectedCar={selectedCar}
      isOpenCarList={isOpenCarList}
      linkDriveOwner={linkDriveOwnerInfo}
      news={displayNews}
      categories={displayCategories}
      banners={banners}
      isCategoriesLoading={isCategoriesLoading}
      isBannersLoading={isBannersLoading}
      affiliatesInfo={affiliatesSwitch}
      isShowCarWashService={isShowCarWashService}
      isShowCarAssetValue={isShowCarAssetValue}
      isShowSwitchCar={isShowSwitchCar}
      isShowPrepaidInfo={isShowPrepaidInfo}
      isRegisterPaymentWarranty={isRegisterPaymentWarranty}
      isWarrantyExpired={isWarrantyExpired}
      isWarrantiesMissDocument={isWarrantiesMissDocument}
      warrantyStatusText={warrantyStatusText}
      notificationCount={notificationCount}
      hasSubscription={hasSubscription}
      hasSubscriptionNotice={hasSubscriptionNotice}
      subscriptionNoticeId={subscriptionNoticeId}
      // 顧客情報の再取得中かどうかは見ない。onPageAfterIn 等で走る通常の
      // 再取得までスケルトンに戻すと、内容が変わらないのに二度点滅するため。
      // 判定に必要なのは「ショッピング先とズレているか」だけ。
      subscriptionLoaded={subscriptionLoaded}
      subscriptionStatus={subscriptionStatusVal}
      subscriptionApplyStatus={subscriptionApplyStatus}
      subscriptionInheritanceStatus={subscriptionInheritanceStatus}
      subscriptionTStockCarId={subscriptionTStockCarId}
      subscriptionId={subscriptionId}
      isShowSubscription={isShowSubscription}
      handleClickRegistCar={handleClickRegistCar}
      changeIsOpenCarList={setIsOpenCarList}
      handleClickListImage={handleClickListImage}
      handleClickMaintenanceButton={handleClickMaintenanceButton}
      handleClickNavigationNews={handleClickNavigationNews}
      handleClickNews={handleClickNews}
      handlePaymentRegistAlert={handlePaymentRegistAlert}
      handlePaymentRegistAlertPrepaid={handlePaymentRegistAlertPrepaid}
      onRefetch={handleOnRefech}
      selectedTenant={selectedTenant}
      proactiveMessage={proactiveMessage}
    />
  );
};
